import {FC, useMemo, useState} from 'react'
import {APP_LANGUAGES, changeAppLanguage, useTranslation} from '@/translations/i18n.tsx'
import {SearchLgIcon} from '@components/ui/icon'
import {InputText} from '@components/commons/input-text'
import {cn} from '@utilities/helpers'
import {ModalCloseButton} from '@components/ui/modal-atoms'
import {Dialog, DialogClose, DialogContent} from '@components/ui/dialog'

interface LangModalProps {
    onCloseCb: () => void
}

export const LangSelectionModal: FC<LangModalProps> = ({onCloseCb}) => {
    const {
        t,
        i18n: {language}
    } = useTranslation()
    const currentLanguage = APP_LANGUAGES.find(lang => lang.code == language) ?? APP_LANGUAGES[0]
    const [search, setSearch] = useState('')

    const languagesList = useMemo(() => {
        return [currentLanguage, ...APP_LANGUAGES.filter(lang => lang.code != currentLanguage.code)].filter(
            lang =>
                lang.label.toLowerCase().includes(search.toLowerCase()) ||
                lang.code.toLowerCase().includes(search.toLowerCase())
        )
    }, [currentLanguage, search])

    return (
        <Dialog open onOpenChange={open => !open && onCloseCb()}>
            <DialogContent className="flex w-[850px] flex-col" showCloseButton={false}>
                <div className="sticky top-0 flex w-full flex-wrap items-center gap-2 border-b border-neutral-200 bg-white p-4">
                    <div className="flex grow items-center gap-2">
                        <DialogClose render={<ModalCloseButton size="lg" />} />
                        <h3 className="shrink-0 text-xl font-bold text-neutral-900">
                            {t('commons:select_language_title')}
                        </h3>
                    </div>

                    {APP_LANGUAGES.length > 12 && (
                        <InputText
                            className="w-[unset]"
                            inputSize="sm"
                            placeholder={String(t('commons:select_language_search'))}
                            type="text"
                            startSlot={<SearchLgIcon size={20} fill="var(--color-neutral-500)" />}
                            onChange={event => setSearch(event.target.value)}
                        />
                    )}
                </div>

                {languagesList.length >= 1 ? (
                    <div className="grid h-max max-h-full w-full grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 overflow-y-auto p-6">
                        {languagesList.map(lang => (
                            <button
                                className={cn(
                                    'cursor-pointer rounded-lg px-4 py-2.5 text-sm text-neutral-900 transition-colors text-left',
                                    lang.code == currentLanguage.code ? 'border border-black' : 'hover:bg-neutral-50'
                                )}
                                onClick={() => changeAppLanguage(lang.code)}
                                key={lang.code}
                            >
                                {lang.label}
                            </button>
                        ))}
                    </div>
                ) : (
                    <div className="flex h-full w-full items-center justify-center p-6">
                        {t('commons:select_language_no_results')}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    )
}

LangSelectionModal.displayName = 'LangSelectionModal'
